import { useCallback, useEffect } from 'react';
import { useFetcher } from '@remix-run/react';
import { useLatest } from 'ahooks';
import {
    BLOCK_USER_API_ROUTE,
    type action,
} from '~/routes/remix-api.user.block';

/**
 * Hook to block or unblock users.
 */
export function useBlockUser({
    onBlockSuccess,
    onBlockError,
    onUnblockSuccess,
    onUnblockError,
}: {
    onBlockSuccess?: () => void;
    onBlockError?: (message: string) => void;
    onUnblockSuccess?: () => void;
    onUnblockError?: (message: string) => void;
} = {}) {
    const blockFetcher = useFetcher<typeof action>();
    const unblockFetcher = useFetcher<typeof action>();
    const latestOnBlockSuccess = useLatest(onBlockSuccess);
    const latestOnBlockError = useLatest(onBlockError);
    const latestOnUnblockSuccess = useLatest(onUnblockSuccess);
    const latestOnUnblockError = useLatest(onUnblockError);

    const blockUser = useCallback(
        (memberID: number) => {
            blockFetcher.submit(
                { memberID },
                {
                    action: BLOCK_USER_API_ROUTE,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
        },
        [blockFetcher],
    );

    const unblockUser = useCallback(
        (memberID: number) => {
            unblockFetcher.submit(
                { memberID, unblock: true },
                {
                    action: BLOCK_USER_API_ROUTE,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
        },
        [unblockFetcher],
    );

    // Run callback when block user mutation finishes.
    useEffect(() => {
        if (blockFetcher.data && blockFetcher.state === 'loading') {
            const response = blockFetcher.data.response;
            if (response.__typename === 'BlockUsersResult') {
                latestOnBlockSuccess.current?.();
            } else {
                latestOnBlockError.current?.(response.Message);
            }
        }
    }, [blockFetcher, latestOnBlockSuccess, latestOnBlockError]);

    // Run callback when unblock user mutation finishes.
    useEffect(() => {
        if (unblockFetcher.data && unblockFetcher.state === 'loading') {
            const response = unblockFetcher.data.response;
            if (response.__typename === 'BlockUsersResult') {
                latestOnUnblockSuccess.current?.();
            } else {
                latestOnUnblockError.current?.(response.Message);
            }
        }
    }, [unblockFetcher, latestOnUnblockSuccess, latestOnUnblockError]);

    return {
        /**
         * One of the submit fetchers are submitting.
         */
        isSubmitting:
            blockFetcher.state === 'submitting' ||
            unblockFetcher.state === 'submitting',
        /**
         * Block a user.
         */
        blockUser,
        /**
         * Unblock a user.
         */
        unblockUser,
    };
}
